import merge from 'lodash/merge';
import { combineReducers } from 'redux';
import { normalize } from 'normalizr';

import { articleSchema } from '../../schemas';
import {
  GET_ARTICLE_INFO_PENDING,
  GET_ARTICLE_INFO_FULFILLED,
  GET_ARTICLE_INFO_REJECTED,
  GET_ARTICLE_PUSHES_PENDING,
  GET_ARTICLE_PUSHES_FULFILLED,
  GET_ARTICLE_PUSHES_REJECTED,
  GET_ARTICLE_PUSHES_STATS_PENDING,
  GET_ARTICLE_PUSHES_STATS_FULFILLED,
  GET_ARTICLE_PUSHES_STATS_REJECTED,
  GET_ARTICLE_REPLIES_PENDING,
  GET_ARTICLE_REPLIES_FULFILLED,
  GET_ARTICLE_REPLIES_REJECTED,
} from '../../types';

// State interfaces
interface InfoState {
  article: string | null;
  entities: any;
  isFetching: boolean;
  error: any | null;
}

interface PushesStatsState {
  data: any;
  isFetching: boolean;
  error?: any;
}

interface PushesState {
  article: string | null;
  data: any[];
  stats: PushesStatsState;
  offset: number;
  limit: number;
  total?: number;
  isFetching: boolean;
  error: any | null;
}

interface RepliesState {
  data: {
    id: string | null;
    reply_to: string | null; // (FIXME) should be camel case
    replies: any[];
  };
  error: any | null;
  isFetching?: boolean;
}

interface ArticleState {
  info: InfoState;
  pushes: PushesState;
  replies: RepliesState;
}

// Action interfaces
interface GetArticleInfoPendingAction {
  type: typeof GET_ARTICLE_INFO_PENDING;
}

interface GetArticleInfoFulfilledAction {
  type: typeof GET_ARTICLE_INFO_FULFILLED;
  payload: {
    data: any;
  };
}

interface GetArticleInfoRejectedAction {
  type: typeof GET_ARTICLE_INFO_REJECTED;
  error: any;
}

interface GetArticlePushesPendingAction {
  type: typeof GET_ARTICLE_PUSHES_PENDING;
  meta: {
    article: string;
  };
}

interface GetArticlePushesFulfilledAction {
  type: typeof GET_ARTICLE_PUSHES_FULFILLED;
  payload: {
    data: {
      content: any[];
      total: number;
    };
  };
  meta: {
    article: string;
    offset: number;
    limit: number;
  };
}

interface GetArticlePushesRejectedAction {
  type: typeof GET_ARTICLE_PUSHES_REJECTED;
  error: any;
}

interface GetArticlePushesStatsPendingAction {
  type: typeof GET_ARTICLE_PUSHES_STATS_PENDING;
}

interface GetArticlePushesStatsFulfilledAction {
  type: typeof GET_ARTICLE_PUSHES_STATS_FULFILLED;
  payload: {
    data: any;
  };
}

interface GetArticlePushesStatsRejectedAction {
  type: typeof GET_ARTICLE_PUSHES_STATS_REJECTED;
  error: any;
}

interface GetArticleRepliesPendingAction {
  type: typeof GET_ARTICLE_REPLIES_PENDING;
}

interface GetArticleRepliesFulfilledAction {
  type: typeof GET_ARTICLE_REPLIES_FULFILLED;
  payload: {
    data: {
      id: string | null;
      reply_to: string | null;
      replies: any[];
    };
  };
}

interface GetArticleRepliesRejectedAction {
  type: typeof GET_ARTICLE_REPLIES_REJECTED;
  error: any;
}

type InfoAction = GetArticleInfoPendingAction | GetArticleInfoFulfilledAction | GetArticleInfoRejectedAction;
type PushesAction = GetArticlePushesPendingAction | GetArticlePushesFulfilledAction | GetArticlePushesRejectedAction |
                    GetArticlePushesStatsPendingAction | GetArticlePushesStatsFulfilledAction | GetArticlePushesStatsRejectedAction;
type RepliesAction = GetArticleRepliesPendingAction | GetArticleRepliesFulfilledAction | GetArticleRepliesRejectedAction;

const INITIAL_STATE = {
  INFO: {
    article: null,
    entities: {},
    isFetching: false,
    error: null,
  },
  PUSHES: {
    article: null,
    data: [],
    stats: {
      data: {},
      isFetching: false,
    },
    offset: 0,
    limit: 0,
    isFetching: false,
    error: null,
  },
  REPLIES: {
    data: {
      id: null,
      reply_to: null, // (FIXME) should be camel case
      replies: [],
    },
    error: null,
  },
};

const info = (state: InfoState = INITIAL_STATE.INFO, action: InfoAction): InfoState => {
  switch (action.type) {
    case GET_ARTICLE_INFO_PENDING:
      return {
        ...state,
        isFetching: true,
      };
    case GET_ARTICLE_INFO_FULFILLED: {
      const {
        data,
      } = action.payload;

      if (!data) {
        return INITIAL_STATE.INFO;
      }

      const {
        entities,
        result,
      } = normalize(data, articleSchema);

      return {
        entities: merge(state.entities, entities),
        article: result,
        isFetching: false,
        error: null,
      };
    }
    case GET_ARTICLE_INFO_REJECTED:
      return {
        ...state,
        error: action.error,
        isFetching: false,
      };
    default:
      return state;
  }
};

const pushes = (state: PushesState = INITIAL_STATE.PUSHES, action: PushesAction): PushesState => {
  switch (action.type) {
    case GET_ARTICLE_PUSHES_PENDING:
      if (!state.article || action.meta.article !== state.article) {
        return {
          ...INITIAL_STATE.PUSHES,
          isFetching: true,
        };
      }
      return {
        ...state,
        isFetching: true,
      };
    case GET_ARTICLE_PUSHES_FULFILLED: {
      const {
        article,
        offset,
        limit,
      } = action.meta;

      const {
        data: {
          content,
          total,
        },
      } = action.payload;

      let data = content;

      if (article === state.article && offset > state.offset) {
        data = state.data.concat(data);
      }

      return {
        ...state,
        article,
        data,
        total,
        offset,
        limit,
        isFetching: false,
        error: null,
      };
    }
    case GET_ARTICLE_PUSHES_REJECTED:
      return {
        ...INITIAL_STATE.PUSHES,
        error: action.error,
        isFetching: false,
      };
    case GET_ARTICLE_PUSHES_STATS_PENDING:
      return {
        ...state,
        stats: {
          data: {},
          isFetching: true,
        },
      };
    case GET_ARTICLE_PUSHES_STATS_FULFILLED:
      return {
        ...state,
        stats: {
          data: action.payload.data,
          isFetching: false,
        },
      };
    case GET_ARTICLE_PUSHES_STATS_REJECTED:
      return {
        ...state,
        stats: {
          data: {},
          error: action.error,
          isFetching: false,
        },
      };
    default:
      return state;
  }
};

const replies = (state: RepliesState = INITIAL_STATE.REPLIES, action: RepliesAction): RepliesState => {
  switch (action.type) {
    case GET_ARTICLE_REPLIES_PENDING:
      return {
        ...INITIAL_STATE.REPLIES,
        isFetching: true,
      };
    case GET_ARTICLE_REPLIES_FULFILLED:
      return {
        isFetching: false,
        data: action.payload.data,
        error: null,
      };
    case GET_ARTICLE_REPLIES_REJECTED:
      return {
        ...state,
        isFetching: false,
        error: action.error,
      };
    default:
      return state;
  }
};

export default combineReducers<ArticleState>({
  info,
  pushes,
  replies,
});